<template>
  <component
    :is="tag"
    ref="button"
    :data-type="type"
    :data-variant="variant"
    v-bind="attrs"
    :data-class="additionalButtonClasses"
    :class="[
      twMerge(buttonVariants({ type, variant }), String(inheritedClass || '')),
      additionalButtonClasses || '',
      String(inheritedClass),
    ]"
    @keydown.enter="$emit('click')"
    @mouseleave="button?.blur()"
  >
    <slot v-if="!loading"></slot>

    <slot v-else name="loading">
      <div class="flex h-full w-full items-center justify-center">
        <Spinner :size="4" />
      </div>
    </slot>
  </component>
</template>

<script setup lang="ts">
  import { cva, type VariantProps } from 'class-variance-authority';
  import { twMerge } from 'tailwind-merge';
  import { config, getClientTheme } from 'src/utils/config';
  import Spinner from './Spinner.vue';
  import { computed, useAttrs, useTemplateRef } from 'vue';
  type ButtonProps = VariantProps<typeof buttonVariants>;

  const props = defineProps<{
    type?: ButtonProps['type'];
    variant?: ButtonProps['variant'];
    loading?: boolean;
    tag?: 'a' | 'button';
  }>();
  const attrs = useAttrs();
  const inheritedClass = computed(() => attrs.class || '');
  const componentStyles = computed(() => config.value.theme?.components);
  const additionalButtonClasses = computed(() => {
    if (!props.type) return '';
    if (props.type === 'outline') return '';
    return componentStyles.value?.button?.[props.type] || '';
  });
  const tag = computed(() => props.tag || 'button');
  const button = useTemplateRef<HTMLButtonElement | HTMLAnchorElement>('button');
  const outlineVariants = cva(
    'bg-background text-primary rounded-custom py-3 px-8 border border-primary hover:bg-primary hover:text-white disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-background disabled:hover:text-primary focus:bg-primary focus:text-white',
    {
      variants: {
        type: {
          default: 'body1-bold',
          audi: 'body1 text-fontPrimary border-fontPrimary hover:bg-background hover:text-fontPrimary-400 hover:border-fontPrimary-400 focus:bg-background focus:text-fontPrimary-400 focus:border-fontPrimary-400',
          volkswagen: 'body1',
          bentley: 'body1',
          vwg: 'body1',
          mercedes:
            'body1-bold bg-backgroundSoft border-0 hover:bg-secondary hover:text-primary focus:bg-secondary focus:text-primary',
          bmw: 'body1-bold text-primary hover:bg-background hover:text-primary-700 hover:border-primary-700 focus:bg-background focus:text-primary-700 focus:border-primary-700 disabled:opacity-35',
          mopar: 'action2-bold bg-background uppercase px-6 py-4',
          jeep: 'action2-bold bg-background uppercase px-6 py-4',
          mini: 'body1 text-fontPrimary border-2 border-fontPrimary rounded-full hover:bg-secondary hover:text-white hover:border-secondary focus:bg-secondary focus:text-white focus:border-secondary disabled:opacity-35 disabled:cursor-default',
          jlr: 'uppercase action2-bold text-fontPrimary p-4 focus-visible:shadow-focus disabled:opacity-35',
          common: `w-auto bg-transparent !leading-[1.25rem] action2 font-semibold text-fontPrimary focus-visible:shadow-focus disabled:opacity-35 focus:bg-transparent`,
          referral: 'uppercase action2-bold text-fontPrimary p-4 focus-visible:shadow-focus disabled:opacity-35',
          suzuki:
            'action2-bold border-0 bg-primary text-white uppercase p-4 hover:bg-primary-400 focus:bg-background focus:text-fontPrimary focus-visible:shadow-focus disabled:opacity-35',
          jaguar:
            'action2-bold bg-primary text-white uppercase p-4 border-fontPrimary hover:bg-background hover:text-fontPrimary disabled:hover:bg-primary disabled:hover:text-white focus:bg-background focus:text-fontPrimary focus-visible:shadow-focus disabled:opacity-35',
          tesla:
            'py-2 px-8 bg-secondary text-white border-secondary hover:bg-secondary-700 hover:border-secondary-700 hover:text-background-400 disabled:hover:bg-primary disabled:hover:text-white focus:bg-secondary-700 focus:border-secondary-700 focus:text-white focus-visible:shadow-focus disabled:opacity-35',
          aa: 'shadow-custom action2-bold bg-primary text-white hover:text-white hover:bg-fontPrimary disabled:opacity-35',
          toyota:
            'body1-bold bg-primary text-white border-primary hover:bg-primary-600 hover:border-primary-600 hover:text-background-400 disabled:hover:bg-primary disabled:hover:text-white focus:bg-primary-700 focus:border-primary-700 focus:text-white focus-visible:shadow-focus disabled:opacity-35',
          cinch:
            'rounded-full py-2 px-8 bg-secondary text-white border-secondary hover:bg-[#311C77] hover:border-[#311C77] hover:text-background-400 disabled:hover:bg-primary disabled:hover:text-white focus:bg-secondary-700 focus:border-secondary-700 focus:text-white focus-visible:shadow-focus disabled:opacity-35',
          landrover:
            'body1 bg-primary text-white uppercase p-4 border-fontPrimary hover:bg-background hover:text-fontPrimary disabled:hover:bg-primary disabled:hover:text-white focus:bg-background focus:text-fontPrimary focus-visible:shadow-focus disabled:opacity-35',
          volvo: 'action2-bold bg-background uppercase px-6 py-[18px]',
          crashdaddy:
            'action2-bold bg-primary text-white uppercase px-6 py-[18px] hover:bg-primary-400 hover:border-primary-400',
          samsung: 'action2-bold bg-background uppercase px-6 py-[18px]',
        },
      },
    }
  );

  const ghostVariants = cva(
    'bg-transparent text-fontSecondary-900 p-1 flex items-center hover:bg-backgroundSoft disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent focus:bg-backgroundSoft',
    {
      variants: {
        type: {
          default: '',
          audi: '',
          volkswagen: '',
          bentley: '',
          vwg: '',
          samsung: '',
          mercedes: '',
          bmw: 'focus-visible:bg-backgroundSoft disabled:opacity-35',
          toyota: 'focus-visible:bg-backgroundSoft disabled:opacity-35 text-fontPrimary',
          mopar: 'focus-visible:bg-backgroundSoft disabled:opacity-35',
          jeep: 'focus-visible:bg-backgroundSoft disabled:opacity-35',
          mini: 'rounded-full p-2 disabled:opacity-35 disabled:cursor-default',
          jaguar: 'focus-visible:shadow-focus focus-visible:bg-backgroundSoft disabled:opacity-35',
          tesla: 'border-none focus-visible:shadow-focus focus-visible:bg-backgroundSoft disabled:opacity-35',
          aa: 'focus-visible:shadow-focus !border-backgroundSoft-600 focus-visible:bg-backgroundSoft disabled:opacity-35',
          suzuki: 'focus-visible:shadow-focus focus-visible:bg-backgroundSoft disabled:opacity-35 font-bold',
          cinch: 'border-none focus-visible:shadow-focus focus-visible:bg-backgroundSoft disabled:opacity-35',
          jlr: 'uppercase focus-visible:shadow-focus focus-visible:bg-backgroundSoft disabled:opacity-35',
          common: `border-none focus-visible:shadow-focus focus-visible:bg-backgroundSoft disabled:opacity-35`,
          referral: 'uppercase focus-visible:shadow-focus focus-visible:bg-backgroundSoft disabled:opacity-35',
          landrover: 'text-fontPrimary focus-visible:shadow-focus focus-visible:bg-backgroundSoft disabled:opacity-35',
          volvo: '',
          crashdaddy: 'bg-background hover:bg-background-600 focus:bg-background-600',
        },
      },
    }
  );

  const secondaryVariants = cva(
    'bg-secondary text-white rounded-custom p-3 hover:bg-secondary-400 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-secondary focus:bg-secondary-400',
    {
      variants: {
        type: {
          default: 'body1-bold text-fontPrimary',
          audi: 'body1 text-fontPrimary bg-background border border-fontPrimary hover:bg-background hover:text-fontPrimary-400 hover:border-fontPrimary-400 focus:bg-background focus:text-fontPrimary-400 focus:border-fontPrimary-400',
          samsung:
            'body1 bg-background text-fontPrimary border border-secondary hover:bg-backgroundSoft disabled:hover:bg-background focus:bg-backgroundSoft',
          volkswagen:
            'body1 bg-background text-fontPrimary border border-secondary !rounded-[40px] hover:bg-backgroundSoft disabled:hover:bg-background focus:bg-backgroundSoft',
          bentley:
            'body1 bg-background text-fontPrimary border border-secondary hover:bg-backgroundSoft disabled:hover:bg-background focus:bg-backgroundSoft',
          vwg: 'body1 bg-background text-fontPrimary border border-secondary !rounded-[40px] hover:bg-backgroundSoft disabled:hover:bg-background focus:bg-backgroundSoft',
          mercedes: 'body1-bold bg-white text-fontPrimary',
          bmw: 'body1-bold bg-backgroundSoft text-fontPrimary p-4 border border-backgroundSoft hover:bg-backgroundSoft-600 disabled:opacity-35 disabled:hover:bg-backgroundSoft disabled:hover:text-fontPrimary',
          toyota:
            'body1-bold bg-backgroundSoft text-fontPrimary p-4 border border-backgroundSoft hover:bg-backgroundSoft-600 disabled:opacity-35 disabled:hover:bg-backgroundSoft disabled:hover:text-fontPrimary',
          suzuki:
            'uppercase body1-bold bg-backgroundSoft text-fontPrimary p-4 border border-backgroundSoft hover:bg-backgroundSoft-600 disabled:opacity-35 disabled:hover:bg-backgroundSoft disabled:hover:text-fontPrimary',
          mopar:
            'action2-bold bg-white text-primaryFont px-6 py-4 uppercase border border-gray-200 hover:bg-backgroundSoft',
          jeep: 'action2-bold bg-white text-primaryFont px-6 py-4 uppercase border border-gray-200 hover:bg-backgroundSoft',
          mini: 'body1 bg-backgroundSoft text-fontPrimary border border-backgroundSoft rounded-full hover:bg-backgroundSoft-300 hover:border-backgroundSoft-300 focus:bg-backgroundSoft-300 focus:border-backgroundSoft-300 disabled:opacity-35 disabled:hover:bg-backgroundSoft disabled:hover:text-fontPrimary',
          jlr: 'uppercase action2 bg-transparent text-fontPrimary p-4 border border-primary hover:text-white hover:bg-secondary-400 hover:border-secondary-400 focus-visible:shadow-focus disabled:opacity-35',
          common: `action2 font-semibold border-primary text-primary bg-transparent p-4 border-2 hover:text-white hover:bg-primary-400 hover:border-primary-400 focus-visible:shadow-focus disabled:opacity-35`,
          referral:
            'uppercase action2 bg-transparent text-fontPrimary p-4 border border-primary hover:text-white hover:bg-secondary-400 hover:border-secondary-400 focus-visible:shadow-focus disabled:opacity-35',
          aa: '!p-3 font-bold shadow-custom border border-2 border-fontSecondary bg-background hover:bg-backgroundSoft-600 text-black hover:text-black focus:bg-background-600',
          jaguar:
            'action2-bold bg-backgroundSoft text-fontPrimary p-4 border border-backgroundSoft hover:bg-white hover:border-primary focus:bg-white focus:border-primary focus-visible:shadow-focus disabled:opacity-35 disabled:hover:bg-backgroundSoft disabled:hover:text-fontPrimary',
          tesla:
            'action2-bold bg-backgroundSoft text-fontPrimary p-4 border border-backgroundSoft hover:bg-white hover:border-primary focus:bg-white focus:border-primary focus-visible:shadow-focus disabled:opacity-35 disabled:hover:bg-backgroundSoft disabled:hover:text-fontPrimary',
          cinch:
            '!rounded-full action2-bold bg-backgroundSoft text-fontPrimary p-4 border border-backgroundSoft hover:bg-white hover:border-primary focus:bg-white focus:border-primary focus-visible:shadow-focus disabled:opacity-35 disabled:hover:bg-backgroundSoft disabled:hover:text-fontPrimary',
          landrover:
            'body1 uppercase bg-backgroundSoft text-fontPrimary p-4 border border-backgroundSoft hover:bg-white hover:border-primary focus:bg-white focus:border-primary focus-visible:shadow-focus disabled:opacity-35 disabled:hover:bg-backgroundSoft disabled:hover:text-fontPrimary',
          volvo:
            'action2-bold bg-white text-primaryFont px-6 py-[18px] uppercase border border-gray-200 hover:bg-backgroundSoft',
          crashdaddy:
            'action2-bold bg-white text-primaryFont px-6 py-[18px] uppercase border border-gray-200 hover:bg-backgroundSoft',
        },
      },
    }
  );

  const primaryVariants = cva(
    'bg-primary text-white rounded-custom p-3 hover:bg-primary-400 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-primary focus:bg-primary-400',
    {
      variants: {
        type: {
          default: 'body1-bold',
          suzuki: 'body1-bold uppercase',
          audi: 'body1 bg-fontPrimary text-white hover:bg-fontPrimary-400 focus:bg-fontPrimary-400',
          volkswagen: 'body1 !rounded-[40px]',
          bentley: 'body1 border border-primary hover:border-primary-400',
          vwg: 'body1 !rounded-[40px]',
          samsung: 'body1',
          mercedes: 'body1-bold',
          bmw: 'body1-bold p-4 border border-primary hover:bg-primary-700 focus:bg-primary-700 disabled:opacity-35',
          toyota: 'body1-bold p-4 border border-primary hover:bg-primary-700 focus:bg-primary-700 disabled:opacity-35',
          mopar: 'action2-bold px-6 py-4 uppercase',
          jeep: 'action2-bold px-6 py-4 uppercase',
          mini: 'body1 rounded-full',
          jlr: 'uppercase action2-bold p-4 border border-primary hover:bg-white hover:text-primary focus:bg-white focus:text-primary focus-visible:shadow-focus disabled:opacity-35',
          common: `font-semibold action2 px-12 py-4 border border-2  border-primary hover:bg-white hover:text-primary focus:bg-white focus:text-primary focus-visible:shadow-focus disabled:opacity-35`,
          referral:
            'uppercase action2-bold p-4 border border-primary hover:bg-white hover:text-primary focus:bg-white focus:text-primary focus-visible:shadow-focus disabled:opacity-35',
          jaguar:
            'action2-bold p-4 border border-primary hover:bg-white hover:text-primary focus:bg-white focus:text-primary focus-visible:shadow-focus disabled:opacity-35',
          tesla:
            'border border-secondary hover:bg-secondary-600 bg-secondary focus-visible:shadow-focus disabled:opacity-35',
          aa: 'button-background font-bold',
          cinch:
            'rounded-full border border-secondary hover:bg-[#311C77] hover:border-[#311C77] bg-secondary focus-visible:shadow-focus disabled:opacity-35',
          landrover:
            'body1 uppercase p-4 border border-primary hover:bg-white hover:text-primary focus:bg-white focus:text-primary focus-visible:shadow-focus disabled:opacity-35',
          volvo: 'action2-bold px-6 py-[18px] uppercase',
          crashdaddy: 'action2-bold px-6 py-[18px] uppercase',
        },
      },
    }
  );

  const defaultTupleClasses = 'w-full whitespace-nowrap min-w-min flex-1';
  const buttonVariants = cva('active:scale-95 disabled:active:scale-100 focus:shadow-none focus-visible:outline-none', {
    variants: {
      type: {
        outline: outlineVariants({ type: getClientTheme() }),
        ghost: ghostVariants({ type: getClientTheme() }),
        secondary: secondaryVariants({ type: getClientTheme() }),
        primary: primaryVariants({ type: getClientTheme() }),
      },
      variant: {
        default: '',
        audi: '',
        volkswagen: '',
        bentley: '',
        vwg: '',
        samsung: '',
        mercedes: '',
        bmw: '',
        toyota: '',
        mopar: '',
        jeep: '',
        jlr: '',
        common: '',
        referral: '',
        mini: '',
        jaguar: '',
        landrover: '',
        secondary: '',
        volvo: '',
        tesla: '',
        aa: '',
        suzuki: '',
        cinch: '',
        crashdaddy: '',
      },
    },
    compoundVariants: [
      {
        type: 'outline',
        variant: 'default',
        class: `${defaultTupleClasses} border-backgroundSoft text-fontSecondary hover:border-primary  disabled:hover:backgroundSoft disabled:hover:text-fontSecondary data-[selected=true]:text-primary data-[selected=true]:outline data-[selected=true]:outline-2 data-[selected=true]:-outline-offset-2 data-[selected=true]:outline-primary data-[selected=true]:hover:bg-background data-[selected=true]:focus:bg-background data-[selected=true]:focus-visible:bg-primary data-[selected=true]:focus-visible:text-white`,
      },
      {
        type: 'outline',
        variant: 'audi',
        class: `${defaultTupleClasses} border-secondary text-secondary hover:border-fontPrimary hover:text-fontPrimary disabled:hover:border-secondary disabled:hover:text-secondary data-[selected=true]:text-fontPrimary data-[selected=true]:outline data-[selected=true]:outline-2 data-[selected=true]:-outline-offset-2 data-[selected=true]:outline-fontPrimary data-[selected=true]:hover:border-fontPrimary-400 data-[selected=true]:hover:text-fontPrimary-400 data-[selected=true]:focus:border-fontPrimary-400 data-[selected=true]:focus:text-fontPrimary-400`,
      },
      {
        type: 'outline',
        variant: 'volkswagen',
        class: `${defaultTupleClasses} border-secondary text-secondary hover:border-primary disabled:hover:backgroundSoft disabled:hover:text-fontSecondary data-[selected=true]:text-white data-[selected=true]:bg-primary data-[selected=true]:border-primary`,
      },
      {
        type: 'outline',
        variant: 'bentley',
        class: `${defaultTupleClasses} border-secondary text-secondary hover:border-primary disabled:hover:backgroundSoft disabled:hover:text-fontSecondary data-[selected=true]:text-white data-[selected=true]:bg-primary data-[selected=true]:border-primary`,
      },
      {
        type: 'outline',
        variant: 'vwg',
        class: `${defaultTupleClasses} border-secondary text-secondary hover:border-primary disabled:hover:backgroundSoft disabled:hover:text-fontSecondary data-[selected=true]:text-white data-[selected=true]:bg-primary data-[selected=true]:border-primary`,
      },
      {
        type: 'outline',
        variant: 'samsung',
        class: `${defaultTupleClasses} border-secondary text-secondary hover:border-primary disabled:hover:backgroundSoft disabled:hover:text-fontSecondary data-[selected=true]:text-white data-[selected=true]:bg-primary data-[selected=true]:border-primary`,
      },
      {
        type: 'outline',
        variant: 'mini',
        class: `${defaultTupleClasses} bg-background border-backgroundSoft text-secondary hover:text-secondary hover:border-secondary hover:bg-background focus:text-secondary focus:border-secondary focus:bg-background data-[selected=true]:bg-background data-[selected=true]:text-fontPrimary data-[selected=true]:outline data-[selected=true]:outline-2 data-[selected=true]:-outline-offset-2 data-[selected=true]:outline-fontPrimary data-[selected=true]:disabled:hover:bg-background data-[selected=true]:disabled:hover:text-fontPrimary`,
      },
      {
        type: 'outline',
        variant: 'bmw',
        class: `${defaultTupleClasses} bg-background border-backgroundSoft text-secondary hover:text-secondary hover:border-secondary focus:text-secondary focus:border-secondary data-[selected=true]:bg-secondary data-[selected=true]:text-white data-[selected=true]:disabled:hover:bg-secondary data-[selected=true]:disabled:hover:text-white`,
      },
      {
        type: 'outline',
        variant: 'toyota',
        class: `${defaultTupleClasses} bg-backgroundSoft border-secondary border-2 text-secondary hover:text-secondary-600 hover:border-secondary-600 hover:bg-backgroundSoft-600 focus:bg-backgroundSoft-600 focus:text-fontPrimary focus:border-fontPrimary data-[selected=true]:border-fontPrimary data-[selected=true]:bg-fontPrimary data-[selected=true]:text-background data-[selected=true]:disabled:hover:bg-secondary data-[selected=true]:disabled:hover:text-white`,
      },
      {
        type: 'outline',
        variant: 'mopar',
        class: `${defaultTupleClasses} border-secondary-300 text-fontSecondary data-[selected=true]:outline data-[selected=true]:outline-1 data-[selected=true]:-outline-offset-1 data-[selected=true]:outline-primary data-[selected=true]:focus:bg-background data-[selected=true]:focus:text-fontPrimary`,
      },
      {
        type: 'outline',
        variant: 'jeep',
        class: `${defaultTupleClasses} border-secondary-300 text-fontSecondary data-[selected=true]:outline data-[selected=true]:outline-1 data-[selected=true]:-outline-offset-1 data-[selected=true]:outline-primary data-[selected=true]:focus:bg-background data-[selected=true]:focus:text-fontPrimary`,
      },
      {
        type: 'outline',
        variant: 'mercedes',
        class: `${defaultTupleClasses} bg-background border text-fontPrimary border-fontPrimary hover:bg-secondary hover:text-fontPrimary focus:bg-secondary focus:text-fontPrimary data-[selected=true]:bg-secondary`,
      },
      {
        type: 'outline',
        variant: 'volvo',
        class: `${defaultTupleClasses} border-backgroundSoft-600 text-fontPrimary hover:text-white hover:border-backgroundSoft-600 disabled:hover:text-fontPrimary data-[selected=true]:outline data-[selected=true]:outline-2 data-[selected=true]:-outline-offset-2 data-[selected=true]:outline-primary data-[selected=true]:focus:bg-background data-[selected=true]:focus:text-fontPrimary`,
      },
      {
        type: 'outline',
        variant: 'crashdaddy',
        class: `${defaultTupleClasses} bg-primary-100 border-primary-100 text-primary hover:text-white hover:border-primary-300 disabled:hover:text-fontPrimary data-[selected=true]:outline data-[selected=true]:outline-2 data-[selected=true]:-outline-offset-2 data-[selected=true]:outline-primary data-[selected=true]:focus:bg-primary-100 data-[selected=true]:focus:text-primary`,
      },
      {
        type: 'outline',
        variant: 'jaguar',
        class: `${defaultTupleClasses} normal-case bg-white text-fontPrimary hover:bg-backgroundSoft disabled:hover:bg-white disabled:hover:text-fontPrimary data-[selected=true]:bg-primary data-[selected=true]:text-white data-[selected=true]:disabled:hover:bg-primary data-[selected=true]:disabled:hover:text-white`,
      },
      {
        type: 'outline',
        variant: 'tesla',
        class: `${defaultTupleClasses} border border-primary-300 bg-background text-primary-400 hover:bg-[#311C77] hover:text-white hover:border-primary data-[selected=true]:bg-background data-[selected=true]:text-fontPrimary data-[selected=true]:font-bold data-[selected=true]:outline data-[selected=true]:outline-2 data-[selected=true]:-outline-offset-2 data-[selected=true]:outline-primary focus:bg-background focus:text-fontPrimary focus:border-primary`,
      },
      {
        type: 'outline',
        variant: 'suzuki',
        class: `${defaultTupleClasses} border-backgroundSoft-800 border-2 text-backgroundSoft-800 bg-background  hover:bg-backgroundSoft hover:border-primary-400 hover:text-primary-400 disabled:hover:backgroundSoft disabled:hover:text-fontSecondary data-[selected=true]:text-primary data-[selected=true]:outline data-[selected=true]:outline-2 data-[selected=true]:-outline-offset-2 data-[selected=true]:outline-primary data-[selected=true]:hover:bg-backgroundSoft data-[selected=true]:focus:bg-backgroundSoft data-[selected=true]:focus-visible:bg-primary data-[selected=true]:focus-visible:text-white`,
      },
      {
        type: 'outline',
        variant: 'cinch',
        class: `${defaultTupleClasses} border border-primary-300 bg-background text-primary-400 hover:bg-background hover:text-fontPrimary hover:border-primary data-[selected=true]:bg-background data-[selected=true]:text-fontPrimary data-[selected=true]:font-bold data-[selected=true]:outline data-[selected=true]:outline-2 data-[selected=true]:-outline-offset-2 data-[selected=true]:outline-primary focus:bg-background focus:text-fontPrimary focus:border-primary`,
      },
      {
        type: 'outline',
        variant: 'referral',
        class: `${defaultTupleClasses} border border-primary-300 hover:border-0 data-[selected=true]:hover:border-0 uppercase data-[selected=true]:bg-primary data-[selected=true]:text-white data-[selected=true]:hover:bg-primary-400 hover:bg-primary-300 hover:text-primary`,
      },
      {
        type: 'outline',
        variant: 'jlr',
        class: `${defaultTupleClasses} border border-primary-300 hover:border-0 data-[selected=true]:hover:border-0 uppercase data-[selected=true]:bg-primary data-[selected=true]:text-white data-[selected=true]:hover:bg-primary-400 hover:bg-primary-300 hover:text-primary`,
      },
      {
        type: 'outline',
        variant: 'common',
        class: `${defaultTupleClasses} px-12 py-2 w-auto flex-initial border border-secondary-300 data-[selected=true]:hover:border-0 data-[selected=true]:bg-secondary data-[selected=true]:text-white data-[selected=true]:hover:bg-secondary-400 hover:bg-secondary-300 hover:text-secondary ${componentStyles.value?.button?.yesNo || ''}`,
      },
      {
        type: 'outline',
        variant: 'landrover',
        class: `${defaultTupleClasses} normal-case bg-white text-fontPrimary hover:bg-backgroundSoft disabled:hover:bg-white disabled:hover:text-fontPrimary data-[selected=true]:bg-primary data-[selected=true]:text-white data-[selected=true]:disabled:hover:bg-primary data-[selected=true]:disabled:hover:text-white`,
      },
      {
        type: 'ghost',
        variant: 'default',
        class: 'border-2 border-primary hover:bg-primary-100',
      },
      {
        type: 'outline',
        variant: 'aa',
        class: `${defaultTupleClasses} shadow-custom border-none outline outline-1 outline-fontSecondary data-[selected=true]:outline-2 data-[selected=true]:outline-fontPrimary bg-background hover:bg-backgroundSoft-600 text-fontSecondary hover:text-fontSecondary data-[selected=true]:text-black focus:bg-background-600`,
      },
    ],
    defaultVariants: {
      type: 'outline',
    },
  });
  defineOptions({
    inheritAttrs: false,
  });
</script>

<style scoped></style>
